import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  X,
  Check,
  CheckCheck,
  TrendingUp,
  CheckCircle,
  MessageSquare,
  Calendar,
  UserPlus,
  Users,
  Shield,
  AlertTriangle,
  Info,
  Loader2,
} from 'lucide-react';
import {
  useNotificationStore,
  Notification,
  NotificationType,
  formatNotificationTime,
} from '../stores/notificationStore';

// ============================================================
// NOTIFICATION CENTER
// ============================================================
// Bell icon with dropdown showing in-app notifications
// ============================================================

// Icon mapping for notification types
const TYPE_ICONS: Record<NotificationType, React.ElementType> = {
  campaign_progress: TrendingUp,
  campaign_complete: CheckCircle,
  new_reply: MessageSquare,
  meeting_booked: Calendar,
  team_invite: UserPlus,
  team_joined: Users,
  team_role_change: Shield,
  dedup_alert: AlertTriangle,
  system: Info,
};

const TYPE_COLORS: Record<NotificationType, { icon: string; bg: string }> = {
  campaign_progress: { icon: 'text-blue-600', bg: 'bg-blue-100' },
  campaign_complete: { icon: 'text-green-600', bg: 'bg-green-100' },
  new_reply: { icon: 'text-purple-600', bg: 'bg-purple-100' },
  meeting_booked: { icon: 'text-emerald-600', bg: 'bg-emerald-100' },
  team_invite: { icon: 'text-indigo-600', bg: 'bg-indigo-100' },
  team_joined: { icon: 'text-indigo-600', bg: 'bg-indigo-100' },
  team_role_change: { icon: 'text-amber-600', bg: 'bg-amber-100' },
  dedup_alert: { icon: 'text-orange-600', bg: 'bg-orange-100' },
  system: { icon: 'text-neutral-600', bg: 'bg-neutral-100' },
};

// ============================================================
// NOTIFICATION ITEM COMPONENT
// ============================================================

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDismiss: (id: string) => void;
  onClick: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDismiss,
  onClick,
}) => {
  const Icon = TYPE_ICONS[notification.type] || Info;
  const colors = TYPE_COLORS[notification.type] || TYPE_COLORS.system;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className={`
        group relative p-3 border-b border-neutral-90/10 last:border-0
        hover:bg-neutral-50/50 transition-colors cursor-pointer
        ${!notification.read ? 'bg-primary-40/5' : ''}
      `}
      onClick={onClick}
    >
      <div className="flex gap-3">
        {/* Icon */}
        <div className={`w-9 h-9 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4.5 h-4.5 ${colors.icon}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={`text-sm font-medium ${!notification.read ? 'text-neutral-90' : 'text-neutral-90/80'}`}>
              {notification.title}
            </p>
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="text-[10px] text-neutral-90/50 whitespace-nowrap">
                {formatNotificationTime(notification.created_at)}
              </span>
              {notification.id && (
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                  {!notification.read && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onMarkAsRead(notification.id);
                      }}
                      className="p-1 rounded hover:bg-neutral-90/10 text-neutral-90/50 hover:text-neutral-90"
                      title="Mark as read"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDismiss(notification.id);
                    }}
                    className="p-1 rounded hover:bg-red-50 text-neutral-90/50 hover:text-red-500"
                    title="Dismiss"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>
          <p className="text-xs text-neutral-90/60 mt-0.5 line-clamp-2">
            {notification.message}
          </p>
          {notification.action_label && (
            <span className="inline-block mt-1.5 text-xs text-primary-40 font-medium">
              {notification.action_label} →
            </span>
          )}
        </div>

        {/* Unread indicator */}
        {!notification.read && (
          <div className="absolute left-1 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-primary-40" />
        )}
      </div>
    </motion.div>
  );
};

// ============================================================
// NOTIFICATION CENTER COMPONENT
// ============================================================

const NotificationCenter: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    unreadCount,
    loading,
    hasMore,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    dismiss,
    subscribeToRealtime,
  } = useNotificationStore();

  // Initial fetch and SSE subscription
  useEffect(() => {
    fetchUnreadCount();
    const unsubscribe = subscribeToRealtime();
    return unsubscribe;
  }, [fetchUnreadCount, subscribeToRealtime]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications({ refresh: true });
    }
  }, [isOpen, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Handle notification click
  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read && notification.id) {
      markAsRead(notification.id);
    }

    if (notification.action_url) {
      setIsOpen(false);
      navigate(notification.action_url);
    }
  };

  // Load more notifications
  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchNotifications();
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 max-lg:min-h-11 max-lg:min-w-11 inline-flex items-center justify-center rounded-lg hover:bg-neutral-90/10 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-neutral-90/60" />
        
        {/* Unread Badge */}
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-neutral-90 rounded-full"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-96 max-h-[480px] bg-white rounded-xl shadow-xl border border-neutral-90/10 overflow-hidden z-[60] origin-top-right max-lg:fixed max-lg:inset-x-3 max-lg:top-14 max-lg:mt-0 max-lg:w-[calc(100vw-1.5rem)] max-lg:max-w-none max-lg:origin-top"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-neutral-90/10 flex items-center justify-between bg-neutral-50/50">
              <h3 className="font-semibold text-neutral-90">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={() => markAllAsRead()}
                    className="text-xs text-primary-40 hover:text-primary-50 font-medium flex items-center gap-1"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    Mark all read
                  </button>
                )}
              </div>
            </div>

            {/* Notification List */}
            <div className="overflow-y-auto max-h-[380px]">
              {loading && notifications.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-primary-40 animate-spin" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <div className="w-12 h-12 rounded-full bg-neutral-90/10 flex items-center justify-center mb-3">
                    <Bell className="w-6 h-6 text-neutral-90/40" />
                  </div>
                  <p className="text-sm font-medium text-neutral-90/70">No notifications yet</p>
                  <p className="text-xs text-neutral-90/50 mt-1">
                    You'll see updates about campaigns, replies, and meetings here
                  </p>
                </div>
              ) : (
                <>
                  <AnimatePresence mode="popLayout">
                    {notifications.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onMarkAsRead={markAsRead}
                        onDismiss={dismiss}
                        onClick={() => handleNotificationClick(notification)}
                      />
                    ))}
                  </AnimatePresence>

                  {/* Load More */}
                  {hasMore && (
                    <button
                      onClick={handleLoadMore}
                      disabled={loading}
                      className="w-full py-3 text-xs text-primary-40 hover:text-primary-50 hover:bg-neutral-50/50 font-medium flex items-center justify-center gap-1 border-t border-neutral-90/10"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        'Load more'
                      )}
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Footer - Link to all notifications */}
            {notifications.length > 0 && (
              <div className="px-4 py-2.5 border-t border-neutral-90/10 bg-neutral-50/30">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/app/settings/notifications');
                  }}
                  className="text-xs text-neutral-90/60 hover:text-neutral-90 transition-colors"
                >
                  Manage notification preferences →
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationCenter;
